import React, { useEffect, useState } from "react";
import { ErrorMessage, Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import clsx from "clsx";
import axios from "../../../../../axios-instance";
import Loader from "../../../../../components/Loader/Loader";
import {
  getLoggedInUserId,
  getUserBusinesses,
} from "../../../../../utils/authHelper";
import { getUserProfileData } from "../../../../../actions/myProfileActions";
import { connect } from "react-redux";
import { COMPANY_BASE_URL, notification } from "../../../../../utils/utility";
import OnboardingSetupLayout, {
  OnboardingActions,
  OnboardingFieldError,
} from "../../../../../components/OnboardingSetup/OnboardingSetupLayout";
import {
  WEEKDAYS,
  getLocaleFirstWeekday,
  getLocaleTimeFormat,
  getOnboardingCompanyName,
  orderWeekdays,
  setOnboardingCompany,
  slugifyOrganisation,
} from "../../../../../components/OnboardingSetup/onboardingSteps";

const Schema = Yup.object().shape({
  url_slug: Yup.string()
    .trim()
    .matches(
      /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
      "Use lowercase letters, numbers, and hyphens only."
    )
    .required("Choose a booking link."),
  timezone_id: Yup.string().required("Select a timezone."),
  start_time: Yup.string().required("Set your start time."),
  end_time: Yup.string()
    .required("Set your end time.")
    .test(
      "after-start",
      "End time must be after start time.",
      function (value) {
        const { start_time } = this.parent;
        if (!start_time || !value) {
          return true;
        }
        return value > start_time;
      }
    ),
  week_days: Yup.array()
    .of(Yup.number())
    .min(1, "Select at least one available day."),
});

const ProfileStepTwo = (props) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [pageLoading, setPageLoading] = useState(false);
  const [timezones, setTimezones] = useState([]);
  const suggestedSlug = slugifyOrganisation(getOnboardingCompanyName());
  const [formValues, setFormValues] = useState({
    url_slug: suggestedSlug,
    timezone_id: "",
    time_format: getLocaleTimeFormat(),
    calendar_first_day: getLocaleFirstWeekday(),
    start_time: "09:00",
    end_time: "17:00",
    week_days: [1, 2, 3, 4, 5],
  });

  useEffect(() => {
    if (id) {
      setOnboardingCompany(id);
    }
  }, [id]);

  useEffect(() => {
    const { getUserProfileData: loadProfile } = props;
    axios
      .get("/timezones")
      .then((response) => {
        setTimezones(response.data.data.timezones || []);
      })
      .catch(() => {
        notification.error("Could not load timezones. Refresh and try again.");
      });

    loadProfile({
      data: {},
      onSuccess: function (data) {
        setFormValues((current) => ({
          ...current,
          timezone_id: data.timezone_id ? String(data.timezone_id) : current.timezone_id,
        }));
      },
      onError: function () {},
    });
  }, []);

  const toggleWeekDay = (day, values, setFieldValue) => {
    const next = values.week_days.includes(day)
      ? values.week_days.filter((item) => item !== day)
      : [...values.week_days, day];
    setFieldValue("week_days", next);
  };

  return (
    <>
      <Loader isShowLoader={pageLoading} />
      <Formik
        initialValues={formValues}
        enableReinitialize
        validationSchema={Schema}
        onSubmit={(values) => {
          setPageLoading(true);
          const payload = { ...values };
          if (getUserBusinesses()) {
            payload.business_id = Object.values(getUserBusinesses()).pop();
          }
          payload.provider_id = getLoggedInUserId();
          payload.schedule_details = values.week_days.map((day) => ({
            weekday_id: day,
            type: "weekly",
            on_status: "E",
            schedule_availability: [
              {
                start_time: values.start_time,
                end_time: values.end_time,
              },
            ],
          }));

          axios
            .post("/admin/quick-setup-step-two", payload)
            .then(() => {
              setPageLoading(false);
              navigate("/signup/profile-step-three");
            })
            .catch((error) => {
              setPageLoading(false);
              const message =
                error.response?.data?.message ||
                "Could not save availability. Please try again.";
              notification.error(message);
            });
        }}
      >
        {(formik) => {
          const { values, handleChange, handleSubmit, handleBlur, setFieldValue } =
            formik;
          const availabilityDays = orderWeekdays(values.calendar_first_day);
          const slugHost = COMPANY_BASE_URL.replace(/^https?:\/\//, "").replace(/\/$/, "");

          return (
            <OnboardingSetupLayout
              currentStep={2}
              title="Booking page and hours"
              description="Clients use this link to pick a time."
            >
              <form className="onboarding-setup__form" onSubmit={handleSubmit} noValidate>
                <div className="onboarding-setup__panels">
                  <section className="onboarding-setup__panel">
                    <h2 className="onboarding-setup__panel-title">Booking page</h2>
                    <div className="onboarding-setup__field">
                      <label className="onboarding-setup__label" htmlFor="url_slug">
                        Public booking link <span className="onboarding-setup__req">*</span>
                      </label>
                      <div className="onboarding-setup__slug">
                        <span className="onboarding-setup__slug-prefix" title={COMPANY_BASE_URL}>
                          {slugHost}/
                        </span>
                        <input
                          type="text"
                          className="onboarding-setup__input"
                          name="url_slug"
                          id="url_slug"
                          placeholder="your-name"
                          autoComplete="off"
                          spellCheck="false"
                          value={values.url_slug}
                          onChange={(event) =>
                            setFieldValue(
                              "url_slug",
                              event.target.value.toLowerCase().replace(/\s+/g, "-")
                            )
                          }
                          onBlur={handleBlur}
                          aria-required="true"
                        />
                      </div>
                      <ErrorMessage name="url_slug" component={OnboardingFieldError} />
                    </div>
                    <div className="onboarding-setup__field">
                      <label className="onboarding-setup__label" htmlFor="timezone_id">
                        Timezone <span className="onboarding-setup__req">*</span>
                      </label>
                      <select
                        name="timezone_id"
                        id="timezone_id"
                        className="onboarding-setup__select"
                        value={values.timezone_id}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        aria-required="true"
                      >
                        <option value="">Select timezone</option>
                        {timezones.map((item) => (
                          <option key={item.value} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </select>
                      <ErrorMessage name="timezone_id" component={OnboardingFieldError} />
                    </div>
                    <div className="onboarding-setup__field">
                      <span className="onboarding-setup__label" id="time_format_label">
                        Time format
                      </span>
                      <div
                        className="onboarding-setup__segment"
                        role="group"
                        aria-labelledby="time_format_label"
                      >
                        <button
                          type="button"
                          className={clsx("onboarding-setup__chip", {
                            "is-active": values.time_format === "24",
                          })}
                          aria-pressed={values.time_format === "24"}
                          onClick={() => setFieldValue("time_format", "24")}
                        >
                          24-hour
                        </button>
                        <button
                          type="button"
                          className={clsx("onboarding-setup__chip", {
                            "is-active": values.time_format === "12",
                          })}
                          aria-pressed={values.time_format === "12"}
                          onClick={() => setFieldValue("time_format", "12")}
                        >
                          12-hour
                        </button>
                      </div>
                    </div>
                  </section>

                  <section className="onboarding-setup__panel">
                    <h2 className="onboarding-setup__panel-title">Hours</h2>
                    <div className="onboarding-setup__grid onboarding-setup__grid--3">
                      <div className="onboarding-setup__field">
                        <label className="onboarding-setup__label" htmlFor="calendar_first_day">
                          Week starts on
                        </label>
                        <select
                          name="calendar_first_day"
                          id="calendar_first_day"
                          className="onboarding-setup__select"
                          value={values.calendar_first_day}
                          onChange={(event) =>
                            setFieldValue("calendar_first_day", Number(event.target.value))
                          }
                        >
                          {WEEKDAYS.map((day) => (
                            <option key={day.id} value={day.id}>
                              {day.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="onboarding-setup__field">
                        <label className="onboarding-setup__label" htmlFor="start_time">
                          From <span className="onboarding-setup__req">*</span>
                        </label>
                        <input
                          type="time"
                          className="onboarding-setup__input"
                          name="start_time"
                          id="start_time"
                          value={values.start_time}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        <ErrorMessage name="start_time" component={OnboardingFieldError} />
                      </div>
                      <div className="onboarding-setup__field">
                        <label className="onboarding-setup__label" htmlFor="end_time">
                          To <span className="onboarding-setup__req">*</span>
                        </label>
                        <input
                          type="time"
                          className="onboarding-setup__input"
                          name="end_time"
                          id="end_time"
                          value={values.end_time}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        <ErrorMessage name="end_time" component={OnboardingFieldError} />
                      </div>
                    </div>
                    <div className="onboarding-setup__field">
                      <span className="onboarding-setup__label" id="available_days_label">
                        Available days <span className="onboarding-setup__req">*</span>
                      </span>
                      <div
                        className="onboarding-setup__days"
                        role="group"
                        aria-labelledby="available_days_label"
                      >
                        {availabilityDays.map((day) => (
                          <button
                            key={`avail-${day.id}`}
                            type="button"
                            className={clsx("onboarding-setup__day", {
                              "is-active": values.week_days.includes(day.id),
                            })}
                            aria-label={day.label}
                            aria-pressed={values.week_days.includes(day.id)}
                            title={day.label}
                            onClick={() =>
                              toggleWeekDay(day.id, values, setFieldValue)
                            }
                          >
                            {day.short}
                          </button>
                        ))}
                      </div>
                      <ErrorMessage name="week_days" component={OnboardingFieldError} />
                    </div>
                  </section>
                </div>

                <OnboardingActions
                  onBack={() => navigate("/signup/profile-step-one")}
                  submitLabel="Continue"
                />
              </form>
            </OnboardingSetupLayout>
          );
        }}
      </Formik>
    </>
  );
};

export default connect(null, {
  getUserProfileData,
})(ProfileStepTwo);
